/**
 * tm/components/RenameFileList.tsx — 첨부 파일 목록 · 파일 이름 고치기 (2026-10-18 지도사 「첨부 후에도 파일명 클릭해서 수정할 수 있으면」)
 *
 *  파일 이름을 누르면 그 자리에서 고친다 — Enter · 칸 밖을 누르면 저장 · Esc 는 그대로
 *  확장자(.pdf 등)는 따로 두고 이름만 고친다(확장자가 지워져 파일이 안 열리는 일이 없게)
 *  오른쪽 「다운로드」는 고친 이름으로 받는다(서버 /download?fileName=)
 */
import { useEffect, useRef, useState } from 'react';
import type { UploadedFile } from '../types';
import { C, T } from './ui';

const BAD = /[\\/:*?"<>|]/g;   // 파일 이름에 못 쓰는 글자(제어 문자는 아래에서 따로)
/** 이름 → [본 이름, 확장자] — 「상담요약.세계식품.pdf」 → ['상담요약.세계식품', '.pdf'] */
export function splitExt(name: string): [string, string] {
  const m = /^(.*?)(\.[A-Za-z0-9]{1,6})$/.exec(name || '');
  return m && m[1] ? [m[1], m[2]] : [name || '', ''];
}
/** 고친 본 이름 + 확장자 → 새 이름 · 못 쓰는 글자 빼고 · 비면 null */
export function renamed(base: string, ext: string): string | null {
  const b = Array.from(String(base || '')).filter(ch => ch.charCodeAt(0) >= 32).join('').replace(BAD, ' ').replace(/\s+/g, ' ').trim().slice(0, 120);
  return b ? b + ext : null;
}

export default function RenameFileList({ files, onRename, onDownload, onRemove, plain }: {
  files: UploadedFile[];
  /** 저장 — 실패하면 throw(이름을 되돌린다) */
  onRename: (index: number, name: string) => Promise<void>;
  /** 오른쪽 「다운로드」(상담 상세) */
  onDownload?: (f: UploadedFile) => void;
  /** 오른쪽 ✕ 빼기(상담 결과 적는 화면) */
  onRemove?: (index: number) => void;
  /** 밑줄 없는 모양(약속 정보 쪽) */
  plain?: boolean;
}) {
  const [edit, setEdit] = useState<number | null>(null);
  const [val, setVal] = useState('');
  const [busy, setBusy] = useState(false);
  const inp = useRef<HTMLInputElement | null>(null);
  useEffect(() => { if (edit != null && inp.current) { inp.current.focus(); inp.current.select(); } }, [edit]);

  function start(i: number) { if (busy) return; setEdit(i); setVal(splitExt(files[i].name)[0]); }
  async function save() {
    if (edit == null) return;
    const i = edit, [base0, ext] = splitExt(files[i].name), next = renamed(val, ext);
    setEdit(null);
    if (!next || next === files[i].name || val.trim() === base0) return;
    setBusy(true);
    try { await onRename(i, next); } catch { /* 부모가 알린다 */ }
    setBusy(false);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {files.map((f, i) => {
        const [, ext] = splitExt(f.name);
        return (
          <div key={(f.s3_key || '') + i}
            style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 30, padding: '4px 10px', borderRadius: 6, background: '#F8FAFC', border: `1px solid ${edit === i ? C.accent : C.border}`, fontSize: T.xs }}>
            <span aria-hidden="true" style={{ flexShrink: 0 }}>📎</span>
            {edit === i ? (
              <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
                <input ref={inp} value={val} onChange={e => setVal(e.target.value)} aria-label="파일 이름"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void save(); } else if (e.key === 'Escape') { e.preventDefault(); setEdit(null); } }}
                  onBlur={() => void save()}
                  style={{ flex: 1, minWidth: 0, padding: '3px 6px', borderRadius: 4, border: `1px solid ${C.border}`, fontSize: T.xs, outline: 'none', color: C.text }} />
                {ext && <span style={{ flexShrink: 0, color: C.muted }}>{ext}</span>}
              </span>
            ) : (
              <button type="button" onClick={() => start(i)} title="눌러서 파일 이름 고치기" disabled={busy}
                style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'none', padding: 0, cursor: busy ? 'default' : 'text', fontSize: T.xs,
                  color: C.accent, fontWeight: plain ? 500 : 400, textDecoration: plain ? 'none' : 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</button>
            )}
            {edit === i
              ? <span style={{ flexShrink: 0, fontSize: T.xs, color: C.muted }}>Enter 저장</span>
              : <>
                  {/* 이름 고치기 — 이름을 눌러도 되지만 눈에 보이게(2026-10-18 「파일명 왜 안 바뀌어」) */}
                  <button type="button" onClick={() => start(i)} disabled={busy} aria-label={`${f.name} 이름 고치기`}
                    style={{ flexShrink: 0, border: 'none', background: 'none', padding: '2px 4px', fontSize: T.xs, color: C.muted, cursor: busy ? 'default' : 'pointer' }}>이름 고치기</button>
                  {onDownload && <button type="button" onClick={() => onDownload(f)} disabled={!f.s3_key}
                    style={{ flexShrink: 0, border: 'none', background: 'none', padding: '2px 0 2px 6px', fontSize: T.xs, color: C.muted, cursor: f.s3_key ? 'pointer' : 'default' }}>다운로드</button>}
                  {onRemove && <button type="button" onClick={() => onRemove(i)} aria-label={`${f.name} 빼기`}
                    style={{ flexShrink: 0, border: 'none', background: 'none', padding: '2px 0 2px 6px', fontSize: T.xs, color: '#DC2626', cursor: 'pointer' }}>✕</button>}
                </>}
          </div>
        );
      })}
    </div>
  );
}
